import dayjs from '~/timeInit';

import type { GridFilterModel } from '@mui/x-data-grid-pro';

import { getCountryObjs } from '@luxuryescapes/lib-countries';

import type { Dayjs } from '~/services/TimeService';

// The participation table lands two days behind (report_date is UTC and loads the next morning).
export const LATEST_REPORT_DAYS_AGO = 2;
export const DEFAULT_RANGE_DAYS = 14;
export const MAX_RANGE_DAYS = 92;

export type CountryOption = { code: string; label: string };

export const COUNTRY_OPTIONS: Array<CountryOption> = Object.values(getCountryObjs())
  .map((country) => ({ code: country.iso2, label: `${country.iso2} (${country.countryName})` }))
  .sort((a, b) => a.code.localeCompare(b.code));

const COUNTRY_LABELS = new Map(COUNTRY_OPTIONS.map((option) => [option.code, option.label]));

export function formatCountry(code: string) {
  return COUNTRY_LABELS.get(code) ?? code;
}

const MAX_CHIP_COUNTRIES = 3;

export function describeCountryFilter(columnName: string, codes: Array<string>) {
  const shown = codes.slice(0, MAX_CHIP_COUNTRIES).map(formatCountry).join(', ');
  const hidden = codes.length - MAX_CHIP_COUNTRIES;
  return `${columnName} is any of ${shown}${hidden > 0 ? ` +${hidden} more` : ''}`;
}

export function getLatestReportDate(): Dayjs {
  return dayjs().subtract(LATEST_REPORT_DAYS_AGO, 'day').startOf('day');
}

export function getDefaultDateRange(): [Dayjs, Dayjs] {
  const end = getLatestReportDate();
  return [end.subtract(DEFAULT_RANGE_DAYS - 1, 'day'), end];
}

// The report covers the LPP account, which lists bedbank property ids.
export function getPropertyAdminPath(propertyId: string) {
  return `/bedbank/properties/${propertyId}`;
}

export type CountryFilterField = 'userCountry' | 'propertyCountry';

export const COUNTRY_FILTER_FIELDS: Array<CountryFilterField> = ['userCountry', 'propertyCountry'];

// The filter panel allows one filter per column, so each column has at most one item.
export function getCountryFilter(filterModel: GridFilterModel, field: CountryFilterField): Array<string> {
  const item = filterModel.items.find((filterItem) => filterItem.field === field);
  return Array.isArray(item?.value) ? item.value : [];
}

export const PROPERTY_NAME_FIELD = 'propertyName';
// svc-public-offer-feed rejects a longer search.
export const MAX_PROPERTY_NAME_LENGTH = 200;

export type ReportFilterField = typeof PROPERTY_NAME_FIELD | CountryFilterField;

export const REPORT_FILTER_FIELDS: Array<ReportFilterField> = [PROPERTY_NAME_FIELD, ...COUNTRY_FILTER_FIELDS];

// The trimmed search, or undefined while the box is empty.
export function getPropertyNameFilter(filterModel: GridFilterModel): string | undefined {
  const item = filterModel.items.find((filterItem) => filterItem.field === PROPERTY_NAME_FIELD);
  const search = typeof item?.value === 'string' ? item.value.trim() : '';
  return search || undefined;
}

export function getUnfilteredFields(filterModel: GridFilterModel): Array<ReportFilterField> {
  return REPORT_FILTER_FIELDS.filter((field) => !filterModel.items.some((item) => item.field === field));
}
